import React, { useEffect, useState } from 'react';
import {
  IconCircleCheck,
  IconArrowRight,
  IconAlertCircle,
  IconChevronLeft,
  IconCheck,
  IconX,
  IconInfoCircle,
  IconChevronDown,
} from '@tabler/icons-react';
import { Modal, Button, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useLoader } from '../../loader/LoaderContext';
import stripeApiService from '../../services/stripe-subscription.service';
import { useLocation, useNavigate, Link } from 'react-router';
import { delay } from '../../utils/helpers';
import PlanFeaturesPopoverInfo from '../../components/PopoverInfoIcon/PlanFeaturesPopoverInfo';

function ChangeSubscriptionPlan() {
  const [productList, setProductList] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [expandedPlans, setExpandedPlans] = useState({});
  const [newPlan, setNewPlan] = useState(null);
  const [priceDifference, setPriceDifference] = useState(null);
  const [isUpgrade, setIsUpgrade] = useState(false);
  const [billingImpact, setBillingImpact] = useState('');
  const [billingImpactNote, setBillingImpactNote] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();

  const location = useLocation();
  const [organizationAttributes] = useState(location.state?.organization);
  const [isSuperAdmin] = useState(location.state?.isSuperAdmin);

  const currentPlanId = organizationAttributes?.plan_id;
  const subscriptionId = organizationAttributes?.stripe_subscription_id;

  useEffect(() => {
    const fetchProductListData = async () => {
      try {
        showLoader();
        const productListResponse = await stripeApiService.getProductList();

        setProductList(productListResponse.products);

        // Find current plan details
        if (currentPlanId) {
          for (const productInfo of productListResponse.products) {
            const { product, prices } = productInfo;
            const currentPrice = prices.find(
              (price) => price.id === currentPlanId,
            );
            if (currentPrice) {
              setCurrentPlan({
                name: product.name,
                interval: currentPrice.recurring.interval,
                price: currentPrice.unit_amount,
                features: product.marketing_features,
                metadata: product.metadata,
                productDetails: productInfo,
              });
              break;
            }
          }
        }
      } catch (err) {
        console.error('Error while fetching the product list');
        toast.error(
          'Unable to load subscription plans. Please try again later.',
        );
      } finally {
        hideLoader();
      }
    };

    fetchProductListData();
  }, []);

  const handleChoosePlan = (plan, planType) => {
    setBillingImpact('');
    setBillingImpactNote('');

    const productSelected = productList.find(
      (element) => element.product.id === plan.id,
    );

    const priceObject = productSelected.prices.find(
      (p) => p.recurring.interval === planType,
    );

    // Calculate price difference and determine if upgrade or downgrade
    const newPlanInfo = {
      name: plan.name,
      interval: planType,
      price: priceObject.unit_amount,
      priceId: priceObject.id,
      features: plan.marketing_features,
      metadata: plan.metadata,
      productDetails: productSelected,
    };

    setNewPlan(newPlanInfo);

    if (currentPlan) {
      // Normalize to monthly price for comparison (if comparing different intervals)
      const currentMonthlyPrice =
        currentPlan.interval === 'year'
          ? currentPlan.price / 12
          : currentPlan.price;

      const newMonthlyPrice =
        newPlanInfo.interval === 'year'
          ? newPlanInfo.price / 12
          : newPlanInfo.price;

      setIsUpgrade(newMonthlyPrice > currentMonthlyPrice);

      // Calculate price difference
      const diffAmount = Math.abs(newPlanInfo.price - currentPlan.price);
      setPriceDifference(diffAmount);

      // Determine billing impact message
      if (currentPlan.interval === newPlanInfo.interval) {
        // Same billing interval
        if (newMonthlyPrice > currentMonthlyPrice) {
          setBillingImpact(
            `You'll be charged an additional $${formatPrice(diffAmount)} ${planType === 'year' ? 'annually' : 'monthly'}`,
          );
          setBillingImpactNote(
            'A prorated amount will be billed immediately based on your current billing cycle. Taxes may apply.',
          );
        } else {
          setBillingImpact(
            `This change reduces your annual subscription by $${formatPrice(diffAmount)} ${planType === 'year' ? 'annually' : 'monthly'}`,
          );
          setBillingImpactNote(
            'A prorated adjustment will be applied immediately based on your current billing cycle. Taxes may apply.',
          );
        }
      } else {
        // Different billing interval
        setBillingImpact(
          `Your billing frequency cycle will change to ${planType}ly`,
        );
      }
    }

    // Show the confirmation modal
    setShowModal(true);
  };

  const confirmPlanChange = async () => {
    try {
      setIsConfirming(true);
      showLoader();
      // Call API to change subscription plan
      await stripeApiService.changeSubscriptionPlan(
        subscriptionId,
        newPlan.priceId,
        organizationAttributes.user_name,
        newPlan.metadata,
      );

      await delay(3000);
      hideLoader();
      setIsConfirming(false);

      // Close modal
      setShowModal(false);

      // Show success toast
      toast.success(
        `Successfully changed to ${newPlan.name} (${newPlan.interval}ly plan)`,
      );

      if (isSuperAdmin) {
        navigate(
          `/super-admin/organizations/${organizationAttributes?.user_name}/billing`,
        );
      } else {
        // Redirect to subscription billing page
        navigate('/settings/organization/billing?trigger=changePlan');
      }
    } catch (error) {
      hideLoader();
      setIsConfirming(false);

      const errorMessage = error.response?.body
        ? JSON.parse(error.response.body).error
        : 'Failed to change plan. Please try again or contact support.';
      toast.error(errorMessage);
    }
  };

  const formatPrice = (amount) => {
    return (amount / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const toggleFeatures = (productId) => {
    setExpandedPlans(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Highlight text mapping for each plan
  const highlightTexts = {
    'Practice': 'Save 60+ admin hours each month for less than $2/day.',
    'Clinic': 'Give your staff the shared visibility they need to resolve coverage questions faster, reduce callbacks, and keep patient flow moving.',
    'Group': 'Ensure your team gets faster answers, more support, and a consistent, high-level experience across the organization.'
  };

  // Subtitle text mapping for each plan (under seat count)
  const seatSubtitles = {
    'Practice': 'Enough for a provider, an MA, and admin or support staff',
    'Clinic': 'Ideal for multiple providers and support staff.',
    'Group': 'Built for large or multi-site teams across roles.'
  };

  // Plan description mapping
  const planDescriptions = {
    'Practice': 'Perfect for solo practitioners or small practices who want to eliminate coverage confusion and stop losing time to pharmacy callbacks.',
    'Clinic': 'Built for busy clinics where multiple team members handle coverage, prior auth workflows, and patient communication. Keep your entire team aligned with real-time insurance clarity.',
    'Group': 'Designed for multi-site practices and high-volume clinics that need guaranteed speed, coordinated teamwork, and a partnership-level experience.'
  };

  // Helper function to determine badge color based on upgrade/downgrade
  const getBadgeColor = () => {
    return isUpgrade ? '#22C55E' : '#F97316'; // Green for upgrade, orange for downgrade
  };

  // Helper method to check if a plan is active or should be hidden
  const isActivePlan = (productId, prices, planType) => {
    if (!currentPlanId || !currentPlan) return false;

    // Check if this is the current plan
    const priceObject = prices.find(
      (price) => price.recurring.interval === planType,
    );
    if (priceObject?.id === currentPlanId) return true;

    // If current plan is for the same product but different interval, hide it
    return (
      currentPlan.productDetails.product.id === productId &&
      currentPlan.interval !== planType
    );
  };

  // Helper to check if a plan should be hidden
  const shouldHidePlan = (productId, planType) => {
    if (!currentPlan) return false;

    // Hide the plan if it's the same product but different interval
    return (
      currentPlan.productDetails.product.id === productId &&
      currentPlan.interval !== planType
    );
  };

  // Helper to get price based on interval
  const getPriceByInterval = (prices, interval) => {
    return prices.find((price) => price.recurring.interval === interval);
  };

  // Filter function to exclude features not relevant for existing subscribers
  const shouldExcludeFeature = (featureName) => {
    const lowerName = featureName.toLowerCase();
    return (
      lowerName.includes('trial') ||           // Excludes "30-Day Free Trial"
      lowerName.includes('onboarding') ||      // Excludes all onboarding types
      lowerName.includes('free trial') ||
      lowerName.includes('start-up') ||        // Excludes "No Start-up Fees"
      lowerName.includes('startup') ||
      lowerName.includes('start up')
    );
  };

  if (!productList) return null;

  return (
    <div className="innerpage p-0">
      <div className="subscription-top">
        <div
          className="d-flex align-items-center ms-2 text-muted fw-bolder"
          onClick={() => {
            navigate(-1);
          }}
        >
          <IconChevronLeft size={24} className="mx-1" />
          Go Back
        </div>
      </div>
      <div className={'container plan-container '}>
        {productList && (
          <>
            <h1 className="text-center choose-plan">Change Your Plan</h1>
            <p className="text-center">
              Choose the plan that works for your practice
            </p>
            
            {/* Modern Billing Toggle */}
            <div className="billing-toggle-container">
              <div className="billing-toggle">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                  type="button"
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`toggle-btn ${billingCycle === 'annual' ? 'active' : ''}`}
                  type="button"
                >
                  <span>Annual</span>
                  <span className="save-badge">Save 20%</span>
                </button>
              </div>
            </div>

            {/* Unified Pricing Cards */}
            <div className="row my-60 justify-content-center">
              {productList
                .slice()
                .reverse()
                .map(({ product, prices }) => {
                  const interval = billingCycle === 'annual' ? 'year' : 'month';
                  const price = getPriceByInterval(prices, interval);
                  const isCurrentPlan = isActivePlan(product.id, prices, interval);
                  const shouldHide = shouldHidePlan(product.id, interval);
                  const isExpanded = expandedPlans[product.id] || false;

                  // Extract metadata
                  const seatCount = product?.metadata?.No_of_users || product?.metadata?.No_of_providers || '2';
                  const planDescription = product?.description || '';

                  // Skip rendering this plan if it should be hidden
                  if (shouldHide) return null;

                  return (
                    <div
                      key={product.id}
                      className="col-lg-4 col-md-6 col-12"
                    >
                      <div
                        className={`subscription-box ${isCurrentPlan ? 'active-subscription' : ''}`}
                      >
                        {isCurrentPlan && (
                          <div className="current-plan">Current Plan</div>
                        )}
                        <div className="sb-card-header">
                          <h3>{product.name}</h3>
                        </div>
                        <h1>
                          ${formatPrice(price.unit_amount)}{' '}
                          <span>/{interval}</span>
                        </h1>
                        <p className="seat-count"><strong>Includes {seatCount} seats</strong></p>
                        <p className="seat-subtitle">{seatSubtitles[product.name] || ''}</p>
                        <p className="plan-description">{planDescriptions[product.name] || planDescription}</p>
                        
                        {/* Collapsible Learn More Section */}
                        <div className="collapsible-section">
                          <button
                            onClick={() => toggleFeatures(product.id)}
                            className="btn btn-outline-secondary learn-more-toggle"
                            type="button"
                          >
                            <span>{isExpanded ? 'Hide Details' : 'Learn More'}</span>
                            <IconChevronDown 
                              size={16} 
                              className={`chevron ${isExpanded ? 'expanded' : ''}`}
                            />
                          </button>
                          
                          {/* Collapsible Feature List */}
                          {isExpanded && (
                            <div className="feature-list-expanded">
                              <ul className="plan-list">
                                {product.marketing_features
                                  .filter(feature => !shouldExcludeFeature(feature.name))
                                  .map((feature, index) => (
                                    <li key={index}>
                                      <IconCheck size={16} />
                                      {feature.name}
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        
                        {/* Highlight Text */}
                        <p className="plan-highlight-text">
                          {highlightTexts[product.name] || ''}
                        </p>
                        
                        {product?.metadata?.footer_text && (
                          <p className="plan-footer-text">
                            {product.metadata.footer_text}
                          </p>
                        )}
                        <div>
                          <button
                            onClick={() =>
                              handleChoosePlan(product, interval)
                            }
                            className={`btn ${isCurrentPlan ? 'btn-primary' : 'btn-outline-primary'}`}
                            disabled={isCurrentPlan}
                          >
                            {isCurrentPlan
                              ? 'Current Plan'
                              : 'Switch Plan'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            
            {/* Contact Support Message */}
            <div className="text-center mt-5 mb-4">
              <p className="text-muted mb-0">
                Need more than 20 seats?{' '}
                <Link 
                  to="/support/feedback" 
                  className="text-primary text-decoration-none fw-medium"
                >
                  Contact Support
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
      {/* Enhanced Professional Confirmation Modal */}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
        className="cst-modal subscription-change-modal"
      >
        <Modal.Header>
          <Modal.Title style={{ color: '#3B82F6', fontSize: '20px', fontWeight: '600' }} as={'h5'}>
            Review Your Plan Change
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: '#6B7280', fontSize: '15px', marginBottom: '24px' }}>
            You're about to update your subscription. Please review the details below.
          </p>
          {currentPlan && newPlan && (
            <>
              <div style={{ padding: '20px', border: '1px solid #E5E7EB', borderRadius: '8px', marginBottom: '24px', backgroundColor: '#fff' }}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="plan-change-box" style={{ flex: 1 }}>
                    <div style={{ color: '#6B7280', fontSize: '13px', marginBottom: '4px' }}>Current Plan</div>
                    <h5 style={{ marginBottom: '8px', color: '#111827', fontSize: '18px', fontWeight: '600' }}>{currentPlan.name}</h5>
                    <span style={{ 
                      display: 'inline-block',
                      padding: '4px 12px', 
                      backgroundColor: '#6B7280', 
                      color: '#fff', 
                      borderRadius: '12px', 
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {currentPlan.interval}ly
                    </span>
                    <div style={{ marginTop: '8px', fontWeight: '600', fontSize: '20px', color: '#111827' }}>
                      ${formatPrice(currentPlan.price)}/{currentPlan.interval}
                    </div>
                  </div>

                  <div className="text-center" style={{ padding: '0 20px' }}>
                    <IconArrowRight size={24} style={{ color: '#9CA3AF' }} />
                  </div>

                  <div className="plan-change-box" style={{ flex: 1 }}>
                    <div style={{ color: '#6B7280', fontSize: '13px', marginBottom: '4px' }}>New Plan</div>
                    <h5 style={{ marginBottom: '8px', color: '#111827', fontSize: '18px', fontWeight: '600' }}>{newPlan.name}</h5>
                    <span style={{ 
                      display: 'inline-block',
                      padding: '4px 12px', 
                      backgroundColor: '#3B82F6', 
                      color: '#fff', 
                      borderRadius: '12px', 
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {newPlan.interval}ly
                    </span>
                    <div style={{ marginTop: '8px', fontWeight: '600', fontSize: '20px', color: '#111827' }}>
                      ${formatPrice(newPlan.price)}/{newPlan.interval}
                    </div>
                  </div>
                </div>

                <div style={{ 
                  padding: '16px', 
                  backgroundColor: '#DBEAFE', 
                  borderRadius: '8px', 
                  marginTop: '16px',
                  border: '1px solid #BFDBFE'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <IconInfoCircle size={20} style={{ color: '#3B82F6', marginTop: '2px', flexShrink: 0 }} />
                    <div style={{ fontSize: '14px', color: '#1E40AF' }}>
                      <strong style={{ fontWeight: '600' }}>Billing Impact:</strong> {billingImpact}
                      {billingImpactNote && (
                        <div style={{ marginTop: '8px', fontStyle: 'italic', fontSize: '13px', color: '#1E40AF' }}>
                          {billingImpactNote}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Features Comparison */}
              <div style={{ marginBottom: '24px' }}>
                <h6 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
                  What&apos;s Changing in Your Subscription
                </h6>
                
                {(() => {
                  // Get feature names from both plans
                  const currentFeatures = (currentPlan.features || []).map(f => f.name);
                  const newFeatures = (newPlan.features || []).map(f => f.name);
                  
                  // Filter function to exclude features not relevant for existing subscribers
                  const shouldExcludeFeature = (featureName) => {
                    const lowerName = featureName.toLowerCase();
                    return (
                      lowerName.includes('trial') ||           // Excludes "30-Day Free Trial"
                      lowerName.includes('onboarding') ||      // Excludes "Onboarding"
                      lowerName.includes('free trial') ||
                      lowerName.includes('start-up') ||        // Excludes "No Start-up Fees"
                      lowerName.includes('startup') ||
                      lowerName.includes('start up')
                    );
                  };
                  
                  // Categorize features
                  const categorizeFeature = (featureName) => {
                    const lowerName = featureName.toLowerCase();
                    if (lowerName.includes('support')) return 'support';
                    if (lowerName.includes('feature request') || lowerName.includes('beta feature')) return 'featureRequests';
                    if (lowerName.includes('provider') || lowerName.includes('seat')) return 'seats';
                    return 'core';
                  };
                  
                  // Group features by category
                  const groupedFeatures = {
                    core: [],
                    support: { current: null, new: null },
                    featureRequests: { current: null, new: null }
                  };
                  
                  // Process current plan features
                  currentFeatures.forEach(feature => {
                    // Skip features that are not relevant for existing subscribers
                    if (shouldExcludeFeature(feature)) return;
                    
                    const category = categorizeFeature(feature);
                    if (category === 'core') {
                      groupedFeatures.core.push(feature);
                    } else if (category !== 'seats') {
                      groupedFeatures[category].current = feature;
                    }
                  });
                  
                  // Process new plan features
                  newFeatures.forEach(feature => {
                    // Skip features that are not relevant for existing subscribers
                    if (shouldExcludeFeature(feature)) return;
                    
                    const category = categorizeFeature(feature);
                    if (category === 'core' && !groupedFeatures.core.includes(feature)) {
                      groupedFeatures.core.push(feature);
                    } else if (category !== 'seats') {
                      groupedFeatures[category].new = feature;
                    }
                  });
                  
                  // Get seats data
                  const currentSeats = currentPlan.metadata?.No_of_users || currentPlan.metadata?.No_of_providers || '—';
                  const newSeats = newPlan.metadata?.No_of_users || newPlan.metadata?.No_of_providers || '—';
                  const seatsChanged = currentSeats !== newSeats;
                  
                  // Build rows array in order
                  const rows = [];
                  
                  // 1. Number of Seats row (first)
                  rows.push({
                    type: 'seats',
                    feature: 'Number of Seats',
                    current: currentSeats,
                    new: newSeats,
                    isNew: seatsChanged
                  });
                  
                  // 2. Core features (checkmark rows)
                  groupedFeatures.core.forEach(feature => {
                    rows.push({
                      type: 'core',
                      feature: feature,
                      current: currentFeatures.includes(feature),
                      new: newFeatures.includes(feature),
                      isNew: !currentFeatures.includes(feature) && newFeatures.includes(feature)
                    });
                  });
                  
                  // 3. Support (grouped)
                  if (groupedFeatures.support.current || groupedFeatures.support.new) {
                    rows.push({
                      type: 'grouped',
                      feature: 'Support',
                      current: groupedFeatures.support.current,
                      new: groupedFeatures.support.new,
                      isNew: groupedFeatures.support.current !== groupedFeatures.support.new
                    });
                  }
                  
                  // 4. Feature Requests (grouped)
                  if (groupedFeatures.featureRequests.current || groupedFeatures.featureRequests.new) {
                    rows.push({
                      type: 'grouped',
                      feature: 'Feature Requests',
                      current: groupedFeatures.featureRequests.current,
                      new: groupedFeatures.featureRequests.new,
                      isNew: groupedFeatures.featureRequests.current !== groupedFeatures.featureRequests.new
                    });
                  }
                  
                  return (
                    <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#F9FAFB' }}>
                            <th style={{ 
                              padding: '12px 16px', 
                              textAlign: 'left', 
                              fontSize: '13px', 
                              fontWeight: '600', 
                              color: '#6B7280',
                              borderBottom: '1px solid #E5E7EB'
                            }}>
                              Feature
                            </th>
                            <th style={{ 
                              padding: '12px 16px', 
                              textAlign: 'center', 
                              fontSize: '13px', 
                              fontWeight: '600', 
                              color: '#6B7280',
                              borderBottom: '1px solid #E5E7EB',
                              width: '280px'
                            }}>
                              Current Plan
                            </th>
                            <th style={{ 
                              padding: '12px 16px', 
                              textAlign: 'center', 
                              fontSize: '13px', 
                              fontWeight: '600', 
                              color: '#6B7280',
                              borderBottom: '1px solid #E5E7EB',
                              width: '280px'
                            }}>
                              New Plan
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, idx) => (
                            <tr key={idx} style={{ 
                              borderBottom: idx < rows.length - 1 ? '1px solid #E5E7EB' : 'none'
                            }}>
                              <td style={{ 
                                padding: '12px 16px', 
                                fontSize: '14px', 
                                color: '#111827',
                                verticalAlign: 'middle'
                              }}>
                                {row.feature}
                              </td>
                              
                              {/* Current Plan Column */}
                              <td style={{ 
                                padding: '12px 16px', 
                                textAlign: 'center',
                                verticalAlign: 'middle'
                              }}>
                                {row.type === 'core' ? (
                                  // Core features: checkmark or dash
                                  row.current ? (
                                    <IconCheck size={20} style={{ color: '#22C55E', display: 'inline-block', verticalAlign: 'middle' }} />
                                  ) : (
                                    <span style={{ color: '#D1D5DB', fontSize: '16px' }}>—</span>
                                  )
                                ) : row.type === 'seats' ? (
                                  // Seats: display number
                                  <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                                    {row.current}
                                  </span>
                                ) : (
                                  // Grouped features: display text or red X
                                  row.current ? (
                                    <span style={{ fontSize: '13px', color: '#111827', whiteSpace: 'nowrap' }}>
                                      {row.current}
                                    </span>
                                  ) : (
                                    <IconX size={20} style={{ color: '#EF4444', display: 'inline-block', verticalAlign: 'middle' }} />
                                  )
                                )}
                              </td>
                              
                              {/* New Plan Column */}
                              <td style={{ 
                                padding: '12px 16px', 
                                textAlign: 'center',
                                verticalAlign: 'middle'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                                  {row.type === 'core' ? (
                                    // Core features: checkmark or X
                                    <>
                                      {row.new ? (
                                        <IconCheck size={20} style={{ color: '#22C55E', display: 'inline-block', verticalAlign: 'middle' }} />
                                      ) : (
                                        <IconX size={20} style={{ color: '#EF4444', display: 'inline-block', verticalAlign: 'middle' }} />
                                      )}
                                      {row.isNew && row.new && (
                                        <span style={{ 
                                          padding: '2px 8px', 
                                          backgroundColor: getBadgeColor(), 
                                          color: '#fff', 
                                          borderRadius: '12px', 
                                          fontSize: '11px',
                                          fontWeight: '600',
                                          whiteSpace: 'nowrap'
                                        }}>
                                          New
                                        </span>
                                      )}
                                    </>
                                  ) : row.type === 'seats' ? (
                                    // Seats: display number with badge if changed
                                    <>
                                      <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                                        {row.new}
                                      </span>
                                      {row.isNew && (
                                        <span style={{ 
                                          padding: '2px 8px', 
                                          backgroundColor: getBadgeColor(), 
                                          color: '#fff', 
                                          borderRadius: '12px', 
                                          fontSize: '11px',
                                          fontWeight: '600',
                                          whiteSpace: 'nowrap'
                                        }}>
                                          New
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    // Grouped features: display text or red X with badge
                                    <>
                                      {row.new ? (
                                        <span style={{ fontSize: '13px', color: '#111827', whiteSpace: 'nowrap' }}>
                                          {row.new}
                                        </span>
                                      ) : (
                                        <IconX size={20} style={{ color: '#EF4444', display: 'inline-block', verticalAlign: 'middle' }} />
                                      )}
                                      {row.isNew && row.new && (
                                        <span style={{ 
                                          padding: '2px 8px', 
                                          backgroundColor: getBadgeColor(), 
                                          color: '#fff', 
                                          borderRadius: '12px', 
                                          fontSize: '11px',
                                          fontWeight: '600',
                                          whiteSpace: 'nowrap'
                                        }}>
                                          New
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            </>
          )}

          <div style={{ paddingTop: '0', paddingBottom: '16px' }}>
            <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '0' }}>
              <strong style={{ fontWeight: '600', color: '#111827' }}>Note:</strong> This change will take effect immediately. You'll be charged a
              prorated difference for the remainder of your billing period.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #E5E7EB', padding: '16px 24px' }}>
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button
              variant="light"
              style={{ 
                padding: '10px 20px',
                border: '1px solid #E5E7EB',
                backgroundColor: '#fff',
                color: '#6B7280',
                fontWeight: '500',
                fontSize: '14px',
                borderRadius: '6px'
              }}
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              style={{ 
                padding: '10px 24px',
                backgroundColor: '#3B82F6',
                border: 'none',
                color: '#fff',
                fontWeight: '500',
                fontSize: '14px',
                borderRadius: '6px'
              }}
              onClick={confirmPlanChange}
              disabled={isConfirming}
            >
              {isConfirming ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Confirming...
                </>
              ) : (
                'Confirm Change'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ChangeSubscriptionPlan;
